import { capitalize, isNumber, isString } from '@art-suite/art-core-ts';
import {
  getEffectiveScanValue,
  getEffectiveScanValueAsNumber,
  getTaskLineItemsInScope,
  getTotalCostIfDefined,
  ScanLineItemOptimized,
  ScanTaskOptimized,
  ScanTemplateValue,
  ScanTemplateValuePresenterNames,
  ScanTemplateValues,
  ScanValues,
} from '@kaiizen/kai-scan-template-system';

import { presentCodeNameAsWords } from './textPresenters';
import { formatUsd } from './valuePresenters';

/**
 * A line item as it is shown: its product, its values and its tags. A line item in a scan is one, and so is a saved
 * version of one.
 */
export type PresentableLineItem = {
  skuDetails?: { brand?: string; title?: string };
  values?: ScanValues;
  tags?: Record<string, unknown>;
  /** A line item in a scan has its totals worked out; a saved version of one doesn't. */
  optimized?: ScanLineItemOptimized['optimized'];
};

/** A modifier as the org names it: the tag it sets on a scan part, and its name. */
export type NamedModifier = { lineItemTag: string; displayName: string };

/** One shown field that differs between two versions of a scan part: its label, then its value before and after. */
export type ScanPartFieldChange = {
  field: string;
  /** Absent when the earlier version had no value, and on a modifier change. */
  before?: string;
  /** Absent when the later version has no value, and on a modifier change. */
  after?: string;
  /** A modifier added or removed ("Deferred added"), which the field names in full. */
  isModifier?: true;
  /** The line item field that moved, on a line item's field change. */
  fieldKey?: LineItemFieldKey;
};

/** The fields a line item shows. */
export type LineItemFieldKey = 'product' | 'measurement' | 'quantity' | 'price' | 'laborPrice' | 'totalCost';

/** A field a line item shows: its label, and its value as shown, undefined when it has none. */
export type LineItemField = { key: LineItemFieldKey; label: string; present: (lineItem: PresentableLineItem) => string | undefined };

/** An amount as a line item shows it: dollars and cents. */
export const presentUsd = (amount: number) => formatUsd(amount, false, true);

/**
 * Measurement displays round up: to the nearest whole number at 10 and above, to the nearest
 * tenth below 10. The epsilon absorbs float noise (425 * 1.1 = 467.50000000000006 reads as 468,
 * not 469).
 */
// `|| 0` keeps zero from rounding to -0 (ceil of -1e-9)
const roundUpMeasurement = (value: number) => (value >= 10 ? Math.ceil(value - 1e-9) : Math.ceil(value * 10 - 1e-9) / 10) || 0;

/** "425 sqft", "4.7 sqft" (count values carry no unit suffix). */
export const presentMeasurementValue = (value: unknown, valueType?: string): string => {
  if (!isNumber(value)) return '';
  const presented = roundUpMeasurement(value).toLocaleString();
  return valueType && valueType !== 'count' ? `${presented} ${valueType}` : presented;
};

/**
 * A group measurement's name, streamlined: room values drop the "room" prefix (roomWallArea → Wall area), and flooring
 * *Area names drop the suffix (tileArea → Tile).
 */
const presentMeasurementName = (valueName: string) =>
  presentCodeNameAsWords((valueName.startsWith('room') ? valueName.replace(/^room/, '') : valueName.replace(/Area$/, '')) || valueName);

/** The measurement field's label: the group measurement it reads, by its streamlined name (roomWallArea → Wall area). */
export const presentMeasurementLabel = (measurementTemplate: ScanTemplateValue) =>
  isString(measurementTemplate.value) ? presentMeasurementName(measurementTemplate.value) : 'Measurement';

/** The quantity field's label: "Qty" for a count, else its unit; a type outside the presenter names map (a custom unit such as "box") labels itself. */
export const presentQuantityLabel = (quantityTemplate: ScanTemplateValue | undefined) =>
  capitalize(
    quantityTemplate?.type === 'count'
      ? 'Qty'
      : ScanTemplateValuePresenterNames[quantityTemplate?.type || ''] || quantityTemplate?.type || '',
  );

/** What each price and cost a line item holds is called wherever it's shown. */
const VALUE_LABELS: Record<string, string> = {
  price: 'Parts',
  laborPrice: 'Labor',
  materialCost: 'Parts total',
  laborCost: 'Labor total',
  totalCost: 'Total',
};

/**
 * A scan value's label, the same wherever the UX shows it: a line item's measurement and quantity by their templates
 * (presentMeasurementLabel, presentQuantityLabel), its prices and costs by name, and any other value by its key in
 * words (overage → Overage, taskDescription → Task description).
 */
export const presentScanValueLabel = (key: string, valueTemplates?: ScanTemplateValues): string => {
  if (key === 'measurement' && valueTemplates?.measurement) return presentMeasurementLabel(valueTemplates.measurement);
  if (key === 'quantity') return presentQuantityLabel(valueTemplates?.quantity) || 'Qty';
  return VALUE_LABELS[key] ?? presentCodeNameAsWords(key);
};

/** A name in an equation: not a function call, which stays as written. */
const EQUATION_VALUE_NAME = /\b[A-Za-z_]\w*\b(?!\s*\()/g;

/**
 * An equation a scan value is computed from, as the UX shows it: each of the part's own values it reads by its label,
 * a group measurement by its streamlined name, and × for multiplication. "price * quantity" reads "Parts × Qty".
 */
export const presentScanValueEquation = (equation: string, valueTemplates?: ScanTemplateValues) =>
  equation
    .replace(
      EQUATION_VALUE_NAME,
      valueName => valueTemplates?.[valueName] ? presentScanValueLabel(valueName, valueTemplates) : presentMeasurementName(valueName),
    )
    .replace(/\*/g, '×');

/** A line item's total as it shows it: its worked-out total in a scan, else its material cost plus its labor cost. */
export const presentLineItemTotal = (lineItem: PresentableLineItem) =>
  presentUsd(
    lineItem.optimized
      ? getTotalCostIfDefined(lineItem as ScanLineItemOptimized) ?? 0
      : (getEffectiveScanValueAsNumber(lineItem.values?.materialCost) ?? 0) + (getEffectiveScanValueAsNumber(lineItem.values?.laborCost) ?? 0),
  );

/** A line item's product as it shows it: its brand and title. */
export const presentLineItemProduct = ({ skuDetails }: PresentableLineItem) => [skuDetails?.brand, skuDetails?.title].filter(Boolean).join(' ') || undefined;

const presentUsdValue = (value: unknown) => isNumber(value) ? presentUsd(value) : undefined;

const presentCount = (value: unknown) => isNumber(value) ? value.toLocaleString() : undefined;

const getValueField = (
  key: LineItemFieldKey,
  valueTemplates: ScanTemplateValues,
  presentValue: (value: unknown) => string | undefined,
): LineItemField => ({
  key,
  label: presentScanValueLabel(key, valueTemplates),
  present: ({ values }) => presentValue(getEffectiveScanValue(values?.[key])),
});

/**
 * The fields a line item shows, in order: its product, then each of its measurement, quantity, parts price and labor
 * price that its template has, then its total. Nothing else a line item holds, such as its supplier or the costs
 * behind its total, is ever shown.
 */
export const getLineItemFields = (valueTemplates: ScanTemplateValues = {}): LineItemField[] => {
  const { measurement, quantity, price, laborPrice } = valueTemplates;
  return [
    { key: 'product', label: 'Product', present: presentLineItemProduct },
    ...(measurement ? [getValueField('measurement', valueTemplates, value => presentMeasurementValue(value, measurement.type) || undefined)] : []),
    ...(quantity ? [getValueField('quantity', valueTemplates, presentCount)] : []),
    ...(price ? [getValueField('price', valueTemplates, presentUsdValue)] : []),
    ...(laborPrice ? [getValueField('laborPrice', valueTemplates, presentUsdValue)] : []),
    { key: 'totalCost', label: presentScanValueLabel('totalCost'), present: presentLineItemTotal },
  ];
};

/** Whether a scan part's tags set a modifier: its tag exactly true, since scan-data tags are loosely typed. */
const tagsSetModifier = (tags: Record<string, unknown> | undefined, lineItemTag: string) => tags?.[lineItemTag] === true;

/**
 * The modifiers a scan part shows, in the org's order: each whose tag it sets. Reads the tags present rather than a
 * fixed pair, so a Display modifier an org minted itself shows up wherever its part does (KAIDEV-4206). Returns the
 * modifiers rather than their names so a caller can key each by its `lineItemTag`, which is unique by construction.
 */
export const getLineItemModifiers = <Modifier extends NamedModifier>(
  tags: Record<string, unknown> | undefined,
  knownModifiers: readonly Modifier[],
): Modifier[] => knownModifiers.filter(modifier => tagsSetModifier(tags, modifier.lineItemTag));

/**
 * Whether a task carries a modifier: one of its line items in the scope (getTaskLineItemsInScope) sets its tag. A tag
 * on work that is not in the scope is not a fact about the scope (KAIDEV-4206). A task row's modifiers and a modifier
 * filter both read this, so a task a filter finds always shows the modifier it was found by.
 */
export const taskCarriesModifierTag = (task: ScanTaskOptimized | undefined, lineItemTag: string) =>
  getTaskLineItemsInScope(task).some(lineItem => tagsSetModifier(lineItem.tags, lineItemTag));

/**
 * Every modifier a task carries, in the org's own order, each once: the task row says which modifiers are in play
 * below it without saying how many lines carry each.
 */
export const getTaskModifiers = <Modifier extends NamedModifier>(
  task: ScanTaskOptimized | undefined,
  knownModifiers: readonly Modifier[],
): Modifier[] => knownModifiers.filter(modifier => taskCarriesModifierTag(task, modifier.lineItemTag));

/**
 * Each modifier set or cleared between two versions of a scan part's tags, in the org's order, named as the org names
 * it: "Deferred added", "Punch list removed". A modifier change carries no values.
 */
export const getModifierChanges = (
  previousTags: Record<string, unknown> | undefined,
  tags: Record<string, unknown> | undefined,
  knownModifiers: readonly NamedModifier[],
): ScanPartFieldChange[] => {
  const previousModifiers = getLineItemModifiers(previousTags, knownModifiers);
  const modifiers = getLineItemModifiers(tags, knownModifiers);
  return knownModifiers
    .filter(modifier => previousModifiers.includes(modifier) !== modifiers.includes(modifier))
    .map(modifier => ({ field: `${modifier.displayName} ${modifiers.includes(modifier) ? 'added' : 'removed'}`, isModifier: true }));
};

/**
 * What differs between two versions of a line item, as it shows them: each modifier set or cleared, then each field
 * whose shown value moved, in the order of getLineItemFields.
 */
export const getLineItemFieldChanges = (
  previousLineItem: PresentableLineItem,
  lineItem: PresentableLineItem,
  valueTemplates: ScanTemplateValues | undefined,
  knownModifiers: readonly NamedModifier[],
): ScanPartFieldChange[] => [
  ...getModifierChanges(previousLineItem.tags, lineItem.tags, knownModifiers),
  ...getLineItemFields(valueTemplates)
    .map(({ key, label, present }) => ({ field: label, fieldKey: key, before: present(previousLineItem), after: present(lineItem) }))
    .filter(({ before, after }) => before !== after),
];
